import React from "react";
import styled from "styled-components";
import { User } from "lucide-react";
import { Link } from "react-router-dom";

interface CastMember {
  id: number;
  name: string;
  character: string;
  profile_path: string | null;
}

interface CastSectionProps {
  cast: CastMember[];
}

const Section = styled.section`
  margin: var(--spacing-6) 0;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  margin-bottom: var(--spacing-4);
  color: var(--color-text-primary);
`;

const CastGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: var(--spacing-4);

  @media (min-width: ${(props) => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
`;

const CastCard = styled(Link)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.2s ease;
  text-decoration: none;
  display: block;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    transform: translateY(-4px);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    &:hover {
      transform: none;
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 150%;
  background: rgba(255, 255, 255, 0.1);
`;

const ActorImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlaceholderIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--color-text-secondary);
`;

const CastInfo = styled.div`
  padding: var(--spacing-2);
`;

const ActorName = styled.h3`
  font-size: 1rem;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-1);
`;

const Character = styled.p`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
`;

export const CastSection = ({ cast }: CastSectionProps) => {
  if (!cast || cast.length === 0) {
    return null;
  }

  const displayCast = cast.slice(0, 12);

  return (
    <Section>
      <Title>Cast</Title>
      <CastGrid>
        {displayCast.map((member) => (
          <CastCard key={member.id} to={`/person/${member.id}`}>
            <ImageContainer>
              {member.profile_path ? (
                <ActorImage
                  src={`https://image.tmdb.org/t/p/w300${member.profile_path}`}
                  alt={member.name}
                  loading="lazy"
                />
              ) : (
                <PlaceholderIcon>
                  <User size={40} />
                </PlaceholderIcon>
              )}
            </ImageContainer>
            <CastInfo>
              <ActorName>{member.name}</ActorName>
              <Character>{member.character}</Character>
            </CastInfo>
          </CastCard>
        ))}
      </CastGrid>
    </Section>
  );
};
